import { useEffect, useRef, useState } from 'react';

const PULL_THRESHOLD = 60; // px the finger must travel before refresh fires
const PULL_MAX = 100; // visual cap on the indicator translation
const VELOCITY_DAMP = 0.5; // 50% rubber-band — fingers travel further than the indicator

interface Options {
  /** Called when the user releases past PULL_THRESHOLD. Must return a Promise. */
  onRefresh: () => Promise<unknown>;
  /** Disable when, e.g., a modal is open. */
  enabled?: boolean;
}

interface State {
  /** Current visual offset in px. 0 = idle, >0 = pulling. */
  offset: number;
  /** True while the refresh promise is in flight. */
  refreshing: boolean;
  /** Ref to attach to the scroll container (or document.body). */
  bind: {
    onTouchStart: (e: React.TouchEvent) => void;
    onTouchMove: (e: React.TouchEvent) => void;
    onTouchEnd: () => void;
  };
}

/**
 * Pull-to-refresh primitive. No deps beyond React. Designed for the Telegram
 * Mini App container: works inside Telegram's iOS/Android WebView, falls back
 * to a no-op on desktop (where window.scrollY === 0 + touch events don't fire
 * meaningfully via mouse).
 *
 * Usage:
 *   const { offset, refreshing, bind } = usePullToRefresh({
 *     onRefresh: () => q.refetch(),
 *   });
 *   return (
 *     <div {...bind} style={{ transform: `translateY(${offset}px)` }}>
 *       {refreshing ? <Spinner /> : null}
 *       …content…
 *     </div>
 *   );
 */
export function usePullToRefresh({ onRefresh, enabled = true }: Options): State {
  const [offset, setOffset] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const startY = useRef<number | null>(null);
  const onRefreshRef = useRef(onRefresh);

  useEffect(() => {
    onRefreshRef.current = onRefresh;
  }, [onRefresh]);

  const handleStart = (e: React.TouchEvent): void => {
    if (!enabled || refreshing) return;
    if (window.scrollY > 0) return;
    const t = e.touches[0];
    if (!t) return;
    startY.current = t.clientY;
  };

  const handleMove = (e: React.TouchEvent): void => {
    if (!enabled || refreshing || startY.current === null) return;
    const t = e.touches[0];
    if (!t) return;
    const dy = t.clientY - startY.current;
    if (dy <= 0) {
      // Scrolling up — release; let the page scroll normally.
      startY.current = null;
      setOffset(0);
      return;
    }
    const damped = Math.min(dy * VELOCITY_DAMP, PULL_MAX);
    setOffset(damped);
  };

  const handleEnd = (): void => {
    if (!enabled || refreshing || startY.current === null) return;
    const pulled = offset;
    startY.current = null;
    if (pulled >= PULL_THRESHOLD) {
      setRefreshing(true);
      setOffset(PULL_THRESHOLD);
      void Promise.resolve(onRefreshRef.current()).finally(() => {
        setRefreshing(false);
        setOffset(0);
      });
    } else {
      setOffset(0);
    }
  };

  return {
    offset,
    refreshing,
    bind: {
      onTouchStart: handleStart,
      onTouchMove: handleMove,
      onTouchEnd: handleEnd,
    },
  };
}
